import { brand, neutral, semantic } from "@/constants/colors";
import { fonts } from "@/constants/fonts";
import { useThemeColor } from "@/hooks/useThemeColor";
import { Ionicons } from "@expo/vector-icons";
import React, { ReactNode, useMemo, memo } from "react";
import {
  StyleProp,
  StyleSheet,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from "react-native";
import ThemedText from "./ThemedText";

type ChipVariant = "filled" | "outlined" | "tonal";
type ChipSize = "sm" | "md" | "lg";
type ChipColor = "primary" | "success" | "warning" | "error" | "neutral";

interface ChipProps {
  label: string;
  variant?: ChipVariant;
  size?: ChipSize;
  color?: ChipColor;
  selected?: boolean;
  disabled?: boolean;
  onPress?: () => void;
  onDelete?: () => void;
  icon?: ReactNode;
  avatar?: ReactNode;
  style?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
}

// Move static objects outside component to prevent recreation
const SIZE_STYLES = {
  sm: { height: 24, fontSize: 12, paddingHorizontal: 8, iconSize: 14 },
  md: { height: 32, fontSize: 14, paddingHorizontal: 12, iconSize: 16 },
  lg: { height: 40, fontSize: 16, paddingHorizontal: 16, iconSize: 18 },
} as const;

const HIT_SLOP = { top: 8, bottom: 8, left: 8, right: 8 };

const Chip = memo(function Chip({
  label,
  variant = "filled",
  size = "md",
  color = "primary",
  selected = false,
  disabled = false,
  onPress,
  onDelete,
  icon,
  avatar,
  style,
  textStyle,
}: ChipProps) {
  const isDark = useThemeColor({}, "background") === "#151718";
  const sizeStyle = SIZE_STYLES[size];

  // Only memoize the color calculation (more complex)
  const colors = useMemo(() => {
    const baseColors = {
      primary: { main: brand.primary.main, faded: brand.primary.faded },
      success: { main: semantic.success.main, faded: semantic.success.faded },
      warning: { main: semantic.warning.main, faded: semantic.warning.faded },
      error: { main: semantic.error.main, faded: semantic.error.faded },
      neutral: {
        main: isDark ? neutral[600] : neutral[400],
        faded: isDark ? neutral[800] : neutral[100]
      },
    };
    return baseColors[color];
  }, [color, isDark]);

  // Simple inline style calculation (faster than useMemo for simple cases)
  const getChipStyle = (): ViewStyle => {
    const baseStyle: ViewStyle = {
      height: sizeStyle.height,
      paddingHorizontal: sizeStyle.paddingHorizontal,
      borderRadius: sizeStyle.height / 2,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
    };

    switch (variant) {
      case "filled":
        return {
          ...baseStyle,
          backgroundColor: selected ? colors.main : colors.faded,
        };
      case "outlined":
        return {
          ...baseStyle,
          backgroundColor: "transparent",
          borderWidth: 1,
          borderColor: selected ? colors.main : isDark ? neutral[600] : neutral[300],
        };
      case "tonal":
        return {
          ...baseStyle,
          backgroundColor: selected ? colors.faded : isDark ? neutral[800] : neutral[100],
        };
    }
  };

  const getTextColor = (): string => {
    if (disabled) return isDark ? neutral[600] : neutral[400];

    switch (variant) {
      case "filled":
        return selected ? "#FFFFFF" : colors.main;
      case "outlined":
        return selected ? colors.main : isDark ? "#FFFFFF" : neutral[900];
      case "tonal":
        return colors.main;
    }
  };

  const textColor = getTextColor();

  const renderLeftContent = () => {
    if (avatar) return <View style={styles.avatar}>{avatar}</View>;
    if (icon) return <View style={styles.icon}>{icon}</View>;
    return null;
  };

  return (
    <TouchableOpacity
      style={[getChipStyle(), disabled && styles.disabled, style]}
      onPress={onPress}
      disabled={disabled || !onPress}
      activeOpacity={0.7}
    >
      {renderLeftContent()}

      <ThemedText
        style={[
          {
            fontSize: sizeStyle.fontSize,
            fontFamily: fonts.medium,
            color: textColor,
          },
          avatar ? styles.textWithAvatar : undefined,
          icon ? styles.textWithIcon : undefined,
          onDelete ? styles.textWithDelete : undefined,
          textStyle,
        ]}
      >
        {label}
      </ThemedText>

      {onDelete && (
        <TouchableOpacity
          onPress={onDelete}
          disabled={disabled}
          style={styles.deleteButton}
          hitSlop={HIT_SLOP}
        >
          <Ionicons
            name="close-circle"
            size={sizeStyle.iconSize}
            color={textColor}
          />
        </TouchableOpacity>
      )}
    </TouchableOpacity>
  );
});

const styles = StyleSheet.create({
  disabled: {
    opacity: 0.5,
  },
  avatar: {
    marginRight: 4,
  },
  icon: {
    marginRight: 2,
  },
  textWithAvatar: {
    marginLeft: 6,
  },
  textWithIcon: {
    marginLeft: 4,
  },
  textWithDelete: {
    marginRight: 4,
  },
  deleteButton: {
    marginLeft: 2,
  },
});

export default Chip;
